// The Passing Report's step 2 (Ben, 2026-10-20): the position average beside each number, as
// Catalyst's Passing page shows its "<Group>s League Season Average". From the league season's Stats
// file (the same one the Radar's scale reads):
// - the group = everyone's minutes at the player's main position in that file (`mainPositions`), the
//   same position rows the Radar's scale uses;
// - each player counts by their passes there, so the average is the position's passes pooled — the
//   Catalyst "league season average" (volume-weighted), not a mean of players (a 7-minute cameo barely
//   moves it). A rate whose own denominator isn't passes (long balls, footed passes, final-third
//   passes) is weighted by passes too: close to, not exactly, the pooled figure.
// The player's own numbers stay their whole season (worked out from their passes, step 1).

import { humanizePositionTemplate } from "../modules/radar-view.js";
import { engineKeyOf } from "./stats.js";
import { groupPlayers, mainPositions, type GroupFile } from "./group-players.js";
import type { PlayerId } from "../data/players.js";

/** The file columns the report sets its numbers beside (engine keys, 0–1 for a %). */
export const PASSING_AVERAGE_KEYS = [
  "pass_success_perc",
  "expected_pass_success_perc",
  "psax_perc",
  "avg_pass_difficulty",
  "hard_pass_perc",
  "medium_pass_perc",
  "easy_pass_perc",
  "hard_pass_success_perc",
  "medium_pass_success_perc",
  "easy_pass_success_perc",
  "forward_pass_perc",
  "sideways_pass_perc",
  "backward_pass_perc",
  "op_f3_forward_pass_perc",
  "op_f3_sideways_pass_perc",
  "op_f3_backward_pass_perc",
  "pass_length",
  "long_ball_perc",
  "average_x_pass",
  "pass_into_pressure_perc",
  "pass_into_danger_perc",
  "left_foot_perc",
] as const;
export type PassingAverageKey = (typeof PASSING_AVERAGE_KEYS)[number];

const WEIGHT = "passes";

export interface PassingAverages {
  /** "Premier League midfielders". */
  label: string;
  /** How many players the average is made from. */
  players: number;
  values: Partial<Record<PassingAverageKey, number | null>>;
}

/** The position average for `playerId`'s main position in this file, or null when they aren't in it.
 *  A column the file doesn't carry is left out (→ no average shown), never thrown. Pure. */
export function passingAverages(group: GroupFile, playerId: PlayerId, leagueLabel: string): PassingAverages | null {
  const template = mainPositions(group.file).get(playerId);
  if (template === undefined) return null;
  const has = new Set(group.file.metricKeys);
  const keys = PASSING_AVERAGE_KEYS.filter((k) => has.has(engineKeyOf(k)));
  if (!has.has(engineKeyOf(WEIGHT))) throw new Error(`Stats file ${group.file.competitionId}/${group.file.seasonId} has no "passes" column`);
  const players = Object.values(groupPlayers(group, "position", [...keys, WEIGHT], leagueLabel)).filter((p) => p.positionTemplate === template);
  const values: PassingAverages["values"] = {};
  for (const k of keys) {
    let sum = 0;
    let weight = 0;
    for (const p of players) {
      const v = p.stats[k];
      const w = p.stats[WEIGHT];
      if (v === null || v === undefined || Number.isNaN(v) || !w) continue;
      sum += v * w;
      weight += w;
    }
    values[k] = weight > 0 ? sum / weight : null;
  }
  return {
    label: `${leagueLabel} ${humanizePositionTemplate(template).toLowerCase()}s`,
    players: new Set(players.map((p) => p.playerId)).size,
    values,
  };
}
